import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
	Building2,
	Plus,
	Search,
	Filter,
	Settings2,
	Trash2,
	UserPlus
} from 'lucide-react';
import {
	Badge,
	Button,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Textarea,
	useToast
} from '@hiring-intelligence/ui';
import { companyApi } from '@/services/api';
import { format } from 'date-fns';
import {
	ListPageShell,
	FilterChipsBar,
	ListToolbarRow,
	CrmDataTable,
	NameCell,
	CrmCreateDrawer,
	FormField,
	type Column,
	type ChipDef
} from '@/components/crm/layout';
import { CRM_MAIN_INDUSTRIES, CRM_INDUSTRY_GROUPS } from '@hiring-intelligence/shared';

const initials = (n?: string | null) => (n ? n.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') : '?');
const dateFmt = (d?: string | Date | null) => { try { return d ? format(new Date(d), 'd MMM yyyy, HH:mm') : null; } catch { return null; } };

const SAVED_VIEWS: { chip: ChipDef; filter: (rows: any[], userId?: string) => any[] }[] = [
	{ chip: { value: 'all', label: 'All companies' }, filter: (rows) => rows },
	{ chip: { value: 'mine', label: 'My companies' }, filter: (rows, uid) => rows.filter((r) => r.ownerId === uid) }
];

interface PicDraft { name: string; email: string }
const emptyCompany = {
	name: '', website: '', linkedinUrl: '', additionalContext: '',
	mainIndustry: '', subIndustry: '',
	pics: [] as PicDraft[]
};

export default function CompaniesPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { toast } = useToast();

	const [view, setView] = useState('all');
	const [search, setSearch] = useState('');
	const [createOpen, setCreateOpen] = useState(false);
	const [draft, setDraft] = useState({ ...emptyCompany });
	const subIndustryOptions = draft.mainIndustry ? CRM_INDUSTRY_GROUPS[draft.mainIndustry] || [] : [];

	const { data, isLoading } = useQuery({
		queryKey: ['companies'],
		queryFn: () => companyApi.list()
	});

	const createMutation = useMutation({
		mutationFn: (d: typeof emptyCompany) => {
			const pics = d.pics
				.map((p) => ({ name: p.name.trim(), email: p.email.trim() }))
				.filter((p) => p.name && p.email);
			return companyApi.create({
				name: d.name,
				website: d.website || undefined,
				linkedinUrl: d.linkedinUrl || undefined,
				mainIndustry: d.mainIndustry || undefined,
				subIndustry: d.subIndustry || undefined,
				additionalContext: d.additionalContext || undefined,
				pics: pics.length > 0 ? pics : undefined
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['companies'] });
			toast({ title: 'Company created', description: 'Research is being generated.' });
		},
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	const submit = (andAddAnother: boolean) => {
		if (!draft.name.trim()) {
			toast({ title: 'Company name required', variant: 'destructive' });
			return;
		}
		createMutation.mutate(draft, {
			onSuccess: () => {
				if (andAddAnother) setDraft({ ...emptyCompany });
				else { setCreateOpen(false); setDraft({ ...emptyCompany }); }
			}
		});
	};

	const allCompanies: any[] = data?.data || [];
	const filtered = useMemo(() => {
		const byView = SAVED_VIEWS.find((v) => v.chip.value === view)?.filter(allCompanies) ?? allCompanies;
		if (!search.trim()) return byView;
		const q = search.toLowerCase();
		return byView.filter((c) => c.name?.toLowerCase().includes(q) || c.industry?.toLowerCase().includes(q));
	}, [allCompanies, view, search]);

	const columns: Column<any>[] = [
		{
			key: 'name',
			header: 'Company Name',
			cell: (c) => (
				<NameCell
					avatar={
						<span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-xs font-semibold text-primary">
							{c.logo ? <img src={c.logo} alt="" className="h-6 w-6 rounded" /> : <Building2 className="h-3.5 w-3.5" />}
						</span>
					}
					name={c.name}
					secondary={c.website?.replace(/^https?:\/\//, '')}
				/>
			)
		},
		{
			key: 'status',
			header: 'Current Status',
			cell: (c) => {
				const hasHRs = (c.hiringRequests?.length ?? 0) > 0;
				return hasHRs
					? <Badge variant="success">Existing Client</Badge>
					: <Badge variant="secondary">Potential Client</Badge>;
			}
		},
		{
			key: 'country',
			header: 'Country / Region',
			cell: (c) => c.location || <span className="text-muted-foreground">—</span>
		},
		{
			key: 'owner',
			header: 'Company Owner',
			cell: (c) => c.owner?.name ? (
				<span className="inline-flex items-center gap-1.5">
					<span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground">{initials(c.owner.name)}</span>
					{c.owner.name}
				</span>
			) : <span className="text-muted-foreground">No owner</span>
		},
		{
			key: 'createdAt',
			header: 'Create Date',
			cell: (c) => <span className="text-muted-foreground">{dateFmt(c.createdAt) || '—'}</span>
		},
		{
			key: 'lastActivity',
			header: 'Last Activity',
			cell: (c) => <span className="text-muted-foreground">{dateFmt(c.lastActivityAt || c.updatedAt) || '—'}</span>
		}
	];

	return (
		<>
			<ListPageShell
				title="Companies"
				subtitle="Choose a company to begin a hiring request. Register new ones if not yet in the directory."
				recordCount={allCompanies.length}
				primaryActions={
					<>
						<Button size="sm" onClick={() => setCreateOpen(true)}>
							<Plus className="mr-1.5 h-4 w-4" /> Create company
						</Button>
					</>
				}
				chips={
					<FilterChipsBar
						chips={SAVED_VIEWS.map((v) => v.chip)}
						activeValue={view}
						onSelect={setView}
						totalViewsLabel={`(${SAVED_VIEWS.length}/50)`}
					/>
				}
				toolbar={
					<ListToolbarRow>
						<div className="relative w-full max-w-sm">
							<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
							<Input placeholder="Search companies…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-8 text-sm" />
						</div>
						<Select disabled>
							<SelectTrigger className="h-9 w-[160px] text-sm"><SelectValue placeholder="Company owner" /></SelectTrigger>
							<SelectContent />
						</Select>
						<Select disabled>
							<SelectTrigger className="h-9 w-[140px] text-sm"><SelectValue placeholder="Lead status" /></SelectTrigger>
							<SelectContent />
						</Select>
						<Button variant="outline" size="sm" disabled>
							<Filter className="mr-1.5 h-3.5 w-3.5" /> Advanced filters
						</Button>
						<div className="ml-auto flex items-center gap-2">
							<Button variant="ghost" size="sm" disabled className="text-xs">Export</Button>
							<Button variant="ghost" size="sm" disabled className="text-xs">
								<Settings2 className="mr-1 h-3.5 w-3.5" /> Edit columns
							</Button>
						</div>
					</ListToolbarRow>
				}
			>
				<CrmDataTable
					columns={columns}
					rows={filtered}
					loading={isLoading}
					onRowClick={(c) => navigate(`/companies/${c.id}`)}
					empty={<div className="text-muted-foreground">No companies match. <button onClick={() => setCreateOpen(true)} className="text-primary hover:underline">Register one →</button></div>}
				/>
			</ListPageShell>

			<CrmCreateDrawer
				open={createOpen}
				onOpenChange={(o) => { setCreateOpen(o); if (!o) setDraft({ ...emptyCompany }); }}
				title="Create company"
				description="Add a company to the directory. Adding PICs now lets you generate a hiring-manager portal link immediately."
				onSubmit={() => submit(false)}
				onSubmitAndAddAnother={() => submit(true)}
				submitting={createMutation.isPending}
			>
				<FormField label="Company name" required>
					<Input placeholder="e.g. Aizen" value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
				</FormField>
				<FormField label="Website">
					<Input placeholder="https://example.com" value={draft.website} onChange={(e) => setDraft((d) => ({ ...d, website: e.target.value }))} />
				</FormField>
				<FormField label="LinkedIn company page">
					<Input placeholder="https://linkedin.com/company/..." value={draft.linkedinUrl} onChange={(e) => setDraft((d) => ({ ...d, linkedinUrl: e.target.value }))} />
				</FormField>
				<FormField label="Main Industry">
					<Select value={draft.mainIndustry || 'none'} onValueChange={(v) => setDraft((d) => ({ ...d, mainIndustry: v === 'none' ? '' : v, subIndustry: '' }))}>
						<SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
						<SelectContent>
							<SelectItem value="none">Not set</SelectItem>
							{CRM_MAIN_INDUSTRIES.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
						</SelectContent>
					</Select>
				</FormField>
				<FormField label="Sub-Industry">
					<Select value={draft.subIndustry || 'none'} onValueChange={(v) => setDraft((d) => ({ ...d, subIndustry: v === 'none' ? '' : v }))} disabled={!draft.mainIndustry}>
						<SelectTrigger><SelectValue placeholder={draft.mainIndustry ? 'Select sub-industry' : 'Pick main industry first'} /></SelectTrigger>
						<SelectContent>
							<SelectItem value="none">Not set</SelectItem>
							{subIndustryOptions.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
						</SelectContent>
					</Select>
				</FormField>
				<FormField label="Additional context" hint="Used to focus the AI-generated company research.">
					<Textarea
						rows={3}
						placeholder="e.g. Focus on recent acquisitions, key products, leadership changes…"
						value={draft.additionalContext}
						onChange={(e) => setDraft((d) => ({ ...d, additionalContext: e.target.value }))}
					/>
				</FormField>
				<FormField label="PIC (Persons In Charge)" hint="Optional — PIC emails grant access to the hiring-manager portal.">
					<div className="space-y-2">
						{draft.pics.map((pic, idx) => (
							<div key={idx} className="flex items-start gap-2">
								<Input
									placeholder="Name"
									value={pic.name}
									onChange={(e) => setDraft((d) => ({ ...d, pics: d.pics.map((p, i) => i === idx ? { ...p, name: e.target.value } : p) }))}
								/>
								<Input
									placeholder="email@example.com"
									type="email"
									value={pic.email}
									onChange={(e) => setDraft((d) => ({ ...d, pics: d.pics.map((p, i) => i === idx ? { ...p, email: e.target.value } : p) }))}
								/>
								<Button
									type="button"
									variant="ghost"
									size="icon"
									className="shrink-0"
									onClick={() => setDraft((d) => ({ ...d, pics: d.pics.filter((_, i) => i !== idx) }))}
								>
									<Trash2 className="h-4 w-4" />
								</Button>
							</div>
						))}
						<Button
							type="button"
							variant="outline"
							size="sm"
							className="w-fit"
							onClick={() => setDraft((d) => ({ ...d, pics: [...d.pics, { name: '', email: '' }] }))}
						>
							<UserPlus className="mr-2 h-4 w-4" /> Add PIC
						</Button>
					</div>
				</FormField>
			</CrmCreateDrawer>
		</>
	);
}
